import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import styled, { useTheme } from "styled-components";
import moment from "moment";
import useMeasure from "utils/hooks/useMeasure";
import { hexToRGB } from "@shared/utils/misc";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { monotonePath, Point } from "common/NewLook/monotonePath.ts";
import { formatDateLabel } from "@shared/utils/date";

export interface ProfitPoint {
  /** Unix seconds. */
  time: number;
  value: number;
}

interface NewLookProfitChartProps {
  points: ProfitPoint[];
  height: number;
  /**
   * "line": the accumulated profit as a smooth line with a soft fill;
   * "bars": the profit of each day as bars from zero.
   */
  mode: "line" | "bars";
  /** Day-long ranges label hours; longer ones label dates. */
  intraday: boolean;
  /** A guide's value; `step` is the distance between two guides. */
  formatAxisValue: (value: number, step: number) => string;
  ariaLabel: string;
  /** The point under the crosshair (undefined when it leaves). */
  onActiveChange?: (point?: ProfitPoint) => void;
  /**
   * The time span the x axis covers (unix seconds), when it is wider than
   * the points (a backtest still running fills its period as it goes).
   */
  timeDomain?: [number, number];
  /**
   * Phone: no value labels, so the line runs edge to edge (the headline
   * above already reads out the figure).
   */
  edgeToEdge?: boolean;
  "data-testid"?: string;
}

const AXIS_WIDTH = 56;
const TOP = 10;
const BOTTOM = 22;

/**
 * New look: the bots' profit over the selected range, drawn like the
 * dashboard's balance chart — three dotted value guides on the right, dates
 * under it, a crosshair (pointer or arrow keys) that marks a point and its
 * time on the axis while the panel's headline reads out the value, so nothing
 * floats over the chart. The series is P&L, so it is coloured by direction:
 * the line by where the range ends (green at or above zero, red below), each
 * bar by its own sign. A solid zero line shows when the range crosses zero.
 */
const NewLookProfitChart = ({
  points,
  height,
  mode,
  intraday,
  formatAxisValue,
  edgeToEdge,
  ariaLabel,
  onActiveChange,
  timeDomain,
  "data-testid": testId,
}: NewLookProfitChartProps) => {
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const gradientId = `profit-${useId().replace(/[^\w-]/g, "")}`;
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const [active, setActive] = useState<number>();
  const isBars = mode === "bars";

  const plotWidth = Math.max(0, width - (edgeToEdge ? 0 : AXIS_WIDTH));
  const plotHeight = height - TOP - BOTTOM;

  const shape = useMemo(() => {
    if (points.length < 2 || !plotWidth) return undefined;
    const values = points.map((p) => p.value);
    let min = Math.min(...values);
    let max = Math.max(...values);
    if (isBars) {
      // Bars grow from zero, so zero is always in view.
      min = Math.min(0, min);
      max = Math.max(0, max);
    }
    if (max === min) {
      const pad = Math.abs(max) * 0.1 || 1;
      min -= pad;
      max += pad;
    } else {
      const pad = (max - min) * (isBars ? 0.08 : 0.12);
      if (!isBars || min < 0) min -= pad;
      max += pad;
    }
    const y = (value: number) =>
      TOP + (1 - (value - min) / (max - min)) * plotHeight;
    // Bars sit in equal slots; a line runs edge to edge. With a time
    // domain, points sit at their time within it.
    const [t0, t1] = timeDomain || [0, 0];
    const timed = !!timeDomain && t1 > t0;
    const slotCount = timed
      ? Math.max(
          points.length,
          Math.round(
            (t1 - t0) /
              Math.max(
                1,
                (points[points.length - 1].time - points[0].time) /
                  (points.length - 1)
              )
          )
        )
      : points.length;
    const slot = plotWidth / slotCount;
    const x = (index: number) => {
      if (timed) {
        const share = (points[index].time - t0) / (t1 - t0);
        const span = isBars ? plotWidth - slot : plotWidth;
        return (isBars ? slot / 2 : 0) + Math.min(1, Math.max(0, share)) * span;
      }
      return isBars
        ? slot * (index + 0.5)
        : (index / (points.length - 1)) * plotWidth;
    };
    const coords: Point[] = points.map((p, index) => [x(index), y(p.value)]);
    const line = monotonePath(coords);
    const last = coords[coords.length - 1];
    const area = `${line}L${last[0]},${TOP + plotHeight}L${coords[0][0]},${
      TOP + plotHeight
    }Z`;
    const guides = [0.15, 0.5, 0.85].map((share) => {
      const value = max - (max - min) * share;
      return { y: y(value), value };
    });
    // The distance between two guides (they sit 0.35 of the range apart).
    const guideStep = (max - min) * (0.5 - 0.15);
    const zeroY = min < 0 && max > 0 ? y(0) : undefined;
    const ticks = [0, 0.33, 0.66, 1].map((share) => {
      if (timed) {
        return { x: share * plotWidth, time: t0 + (t1 - t0) * share, share };
      }
      const index = Math.round((points.length - 1) * share);
      let tickX = x(index);
      if (share === 0) tickX = 0;
      else if (share === 1) tickX = plotWidth;
      return { x: tickX, time: points[index].time, share };
    });
    const barWidth = Math.max(1, Math.min(14, slot * 0.6));
    return {
      coords,
      line,
      area,
      guides,
      guideStep,
      zeroY,
      ticks,
      barWidth,
      y,
      slot,
    };
  }, [points, plotWidth, plotHeight, isBars, timeDomain]);

  const onPointer = (clientX: number, element: HTMLElement) => {
    if (!shape) return;
    const left = element.getBoundingClientRect().left;
    const px = clientX - left;
    let nearest = 0;
    shape.coords.forEach(([cx], index) => {
      if (Math.abs(cx - px) < Math.abs(shape.coords[nearest][0] - px))
        nearest = index;
    });
    setActive(nearest);
  };

  const formatTime = (time: number) =>
    formatDateLabel(moment.unix(time), intraday ? "HH:mm" : "MMM D");
  const formatChipTime = (time: number) =>
    formatDateLabel(
      moment.unix(time),
      intraday ? "MMM D, HH:mm" : "MMM D, YYYY"
    );

  const activePoint = active !== undefined ? points[active] : undefined;
  // Reported when the point changes (not when the callback does).
  const onActiveChangeRef = useRef(onActiveChange);
  onActiveChangeRef.current = onActiveChange;
  useEffect(() => {
    onActiveChangeRef.current?.(activePoint);
  }, [activePoint]);
  // A new series (range, filters) drops a stale crosshair.
  useEffect(() => setActive(undefined), [points]);

  const activeCoord =
    active !== undefined && shape ? shape.coords[active] : undefined;
  const lastCoord = shape?.coords[shape.coords.length - 1];
  const lastValue = points[points.length - 1]?.value ?? 0;
  const lineColor = lastValue < 0 ? tokens.down : tokens.up;
  const fillOpacity = theme.isDarkMode ? 0.2 : 0.12;
  const crosshairColor = hexToRGB(tokens.ink, theme.isDarkMode ? 0.35 : 0.25);

  return (
    <Wrapper
      ref={measureRef}
      style={{ height }}
      role="img"
      aria-label={ariaLabel}
      data-testid={testId}
      tabIndex={shape ? 0 : -1}
      onMouseMove={(event) => onPointer(event.clientX, event.currentTarget)}
      onMouseLeave={() => setActive(undefined)}
      onBlur={() => setActive(undefined)}
      onKeyDown={(event) => {
        if (!shape) return;
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();
          const step = event.key === "ArrowLeft" ? -1 : 1;
          setActive((current) =>
            Math.min(
              shape.coords.length - 1,
              Math.max(0, (current ?? shape.coords.length - 1) + step)
            )
          );
        }
      }}
    >
      {shape && (
        <svg width={width} height={height} aria-hidden={true}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={lineColor}
                stopOpacity={fillOpacity}
              />
              <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          {shape.guides.map((guide) => (
            <g key={guide.y}>
              <line
                x1={0}
                x2={plotWidth}
                y1={guide.y}
                y2={guide.y}
                stroke={tokens.hairlineStrong}
                strokeDasharray="2 4"
              />
              {!edgeToEdge && (
                <AxisText
                  x={plotWidth + 10}
                  y={guide.y + 4}
                  fill={tokens.muted}
                >
                  {formatAxisValue(guide.value, shape.guideStep)}
                </AxisText>
              )}
            </g>
          ))}
          {shape.zeroY !== undefined && (
            <line
              x1={0}
              x2={plotWidth}
              y1={shape.zeroY}
              y2={shape.zeroY}
              stroke={tokens.hairlineStrong}
            />
          )}
          {shape.ticks.map((tick) => (
            <AxisText
              key={tick.share}
              // Edge to edge, the end labels keep the panel's 16px gutter.
              x={
                edgeToEdge
                  ? Math.min(plotWidth - 16, Math.max(16, tick.x))
                  : tick.x
              }
              y={height - 6}
              fill={tokens.muted}
              textAnchor={
                tick.share === 0 ? "start" : tick.share === 1 ? "end" : "middle"
              }
            >
              {formatTime(tick.time)}
            </AxisText>
          ))}
          {activeCoord && (
            <line
              x1={activeCoord[0]}
              x2={activeCoord[0]}
              y1={TOP}
              y2={TOP + plotHeight}
              stroke={crosshairColor}
              strokeWidth={1}
            />
          )}
          {isBars ? (
            points.map((point, index) => {
              const [cx, cy] = shape.coords[index];
              const base = shape.y(0);
              const top = Math.min(cy, base);
              const barHeight = Math.max(1, Math.abs(base - cy));
              const dimmed = active !== undefined && active !== index;
              return (
                <rect
                  key={point.time}
                  x={cx - shape.barWidth / 2}
                  y={top}
                  width={shape.barWidth}
                  height={point.value ? barHeight : 1}
                  rx={Math.min(2, shape.barWidth / 4)}
                  fill={
                    point.value < 0
                      ? tokens.down
                      : point.value > 0
                      ? tokens.up
                      : tokens.hairlineStrong
                  }
                  fillOpacity={dimmed ? 0.35 : 0.85}
                />
              );
            })
          ) : (
            <>
              <path d={shape.area} fill={`url(#${gradientId})`} />
              <path
                d={shape.line}
                fill="none"
                stroke={lineColor}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {lastCoord && !activeCoord && (
                <circle
                  cx={lastCoord[0]}
                  cy={lastCoord[1]}
                  r={3.5}
                  fill={lineColor}
                  stroke={tokens.panel}
                  strokeWidth={2}
                />
              )}
              {activeCoord && (
                <circle
                  cx={activeCoord[0]}
                  cy={activeCoord[1]}
                  r={4.5}
                  fill={lineColor}
                  stroke={tokens.panel}
                  strokeWidth={2}
                />
              )}
            </>
          )}
        </svg>
      )}
      {activePoint && activeCoord && (
        <TimeChip
          style={{
            left: Math.min(Math.max(activeCoord[0], 48), plotWidth - 48),
            top: height - BOTTOM + 3,
          }}
        >
          {formatChipTime(activePoint.time)}
        </TimeChip>
      )}
    </Wrapper>
  );
};

export default React.memo(NewLookProfitChart);

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  outline: none;
  cursor: crosshair;
  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => getNewLookTokens(p.theme).focus};
    border-radius: 6px;
  }
`;
const AxisText = styled.text`
  font-size: 11px;
  font-variant-numeric: tabular-nums;
`;
// Sits on the time axis under the crosshair, over the regular tick labels.
const TimeChip = styled.div`
  position: absolute;
  transform: translateX(-50%);
  padding: 1px 8px;
  border-radius: 6px;
  background: ${(p) => getNewLookTokens(p.theme).ink};
  color: ${(p) => getNewLookTokens(p.theme).panel};
  ${newLookType.meta};
  font-size: 11px;
  line-height: 16px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
`;
